// Tenant branding override → CSS custom properties.
//
// UX brief §Tokens — `--brand-primary` and `--brand-accent` are runtime per-tenant.
// AC2 of the Playwright suite asserts the CSS vars match the override values
// VERBATIM (the test reads the raw computed value), so we set them as-is.
//
// Contrast guardrail (UX open question 1): the AC7 fixture uses
// `accentColor=#ff8c00` which is 2.7:1 against white — would fail WCAG 1.4.3
// for white text on the button. Our chosen mitigation: keep `--brand-accent`
// as a DECORATIVE accent (focus ring outer glow / borders) and place the
// "Sign in" button on `palette.primary` (deep slate, contrast-safe). The CSS
// var is set verbatim per AC2; the white-on-arbitrary-colour problem is
// avoided by NOT painting white text on `--brand-accent`. See LoginPage.tsx.

import type { Tenant } from './types.js';

export function applyBranding(tenant: Tenant | null): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const override = tenant?.brandingOverride;
  if (override?.primaryColor) {
    root.style.setProperty('--brand-primary', override.primaryColor);
  } else {
    root.style.removeProperty('--brand-primary');
  }
  if (override?.accentColor) {
    root.style.setProperty('--brand-accent', override.accentColor);
  } else {
    root.style.removeProperty('--brand-accent');
  }
}

/**
 * WCAG 2.x relative-luminance + contrast helpers. Exposed for future use
 * (e.g. tenant-admin colour-picker validation). Not consumed by the login
 * page itself today — the visual chosen above sidesteps the contrast risk.
 */
export function relativeLuminance(hex: string): number {
  const rgb = parseHex(hex);
  if (!rgb) return 0;
  const [r, g, b] = rgb.map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [light, dark] = la > lb ? [la, lb] : [lb, la];
  return (light + 0.05) / (dark + 0.05);
}

function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const captured = m[1];
  if (!captured) return null;
  const h = captured.length === 3 ? captured.split('').map((c) => c + c).join('') : captured;
  const num = parseInt(h, 16);
  return [(num >> 16) & 0xff, (num >> 8) & 0xff, num & 0xff];
}
